/**
 * Admin product type configs API module.
 *
 * Typed helpers for admin-facing product type config endpoints.
 * Callers (containers, TanStack Query hooks, Redux thunks) MUST import from
 * this module — never call the shared axios client directly.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  ProductTypeConfigListItemDTO,
  ProductTypeConfigPaginationMeta,
  ProductTypeConfigListResponseData,
  ProductTypeConfigResponseData,
  ProductTypeConfigDTO,
  FetchProductTypeConfigsParams,
  CreateProductTypeConfigPayload,
  UpdateProductTypeConfigPayload,
} from '@dfq/shared/types/productTypes';

// ---------------------------------------------------------------------------
// Result shapes returned to callers
// ---------------------------------------------------------------------------

export interface FetchProductTypeConfigsResult {
  productTypeConfigs: ProductTypeConfigListItemDTO[];
  pagination: ProductTypeConfigPaginationMeta;
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

/**
 * Fetch the paginated admin product type configs list.
 *
 * Route: `GET /api/v1/admin/product-type-configs`
 * Auth:  `auth:console`, scope `products:write`
 */
export async function fetchAdminProductTypeConfigs(
  params: FetchProductTypeConfigsParams = {},
): Promise<FetchProductTypeConfigsResult> {
  const {
    page = 1,
    perPage = 25,
    search,
    status,
    sortBy,
    sortDirection,
  } = params;

  try {
    const res = await apiClient.get<ApiEnvelope<Record<string, unknown>>>(
      '/admin/product-type-configs',
      {
        params: {
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(sortBy ? { sort_by: sortBy } : {}),
          ...(sortDirection ? { sort_direction: sortDirection } : {}),
        },
      },
    );

    const envelope = res.data;
    const rawData =
      envelope.data &&
      typeof envelope.data === 'object' &&
      'product_type_configs' in envelope.data
        ? (envelope.data as unknown as ProductTypeConfigListResponseData)
        : null;

    const productTypeConfigs = rawData?.product_type_configs ?? [];

    // Pagination lives at meta.pagination per platform ApiResponse convention.
    // Platform emits snake_case keys (page, per_page, last_page); map to camelCase.
    const rawPag =
      envelope.meta &&
      typeof envelope.meta === 'object' &&
      'pagination' in (envelope.meta as Record<string, unknown>)
        ? ((envelope.meta as Record<string, unknown>).pagination as Record<string, unknown>)
        : null;

    const pagination: ProductTypeConfigPaginationMeta = rawPag
      ? {
          currentPage: ((rawPag['page'] ?? rawPag['currentPage'] ?? page) as number),
          perPage: ((rawPag['per_page'] ?? rawPag['perPage'] ?? perPage) as number),
          total: ((rawPag['total'] ?? productTypeConfigs.length) as number),
          lastPage: ((rawPag['last_page'] ?? rawPag['lastPage'] ?? 1) as number),
        }
      : {
          currentPage: page,
          perPage,
          total: productTypeConfigs.length,
          lastPage: 1,
        };

    return { productTypeConfigs, pagination };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Show (single entity)
// ---------------------------------------------------------------------------

/**
 * Fetch a single ProductTypeConfig for pre-populating the edit form.
 *
 * Route: `GET /api/v1/admin/product-type-configs/{id}`
 * Auth:  `auth:console`, scope `products:write`
 */
export async function getProductTypeConfig(id: number): Promise<ProductTypeConfigDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<Record<string, unknown>>>(
      `/admin/product-type-configs/${id}`,
    );

    const envelope = res.data;
    const rawData =
      envelope.data &&
      typeof envelope.data === 'object' &&
      'product_type_config' in envelope.data
        ? (envelope.data as unknown as ProductTypeConfigResponseData)
        : null;

    if (!rawData?.product_type_config) {
      throw new Error('Unexpected response: missing product_type_config data');
    }

    return rawData.product_type_config;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

/**
 * Create a new ProductTypeConfig record.
 *
 * Route: `POST /api/v1/admin/product-type-configs`
 * Auth:  `auth:console`, scope `products:write`
 */
export async function createProductTypeConfig(
  payload: CreateProductTypeConfigPayload,
): Promise<ProductTypeConfigDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<Record<string, unknown>>>(
      '/admin/product-type-configs',
      payload,
    );

    const envelope = res.data;
    const rawData =
      envelope.data &&
      typeof envelope.data === 'object' &&
      'product_type_config' in envelope.data
        ? (envelope.data as unknown as ProductTypeConfigResponseData)
        : null;

    if (!rawData?.product_type_config) {
      throw new Error('Unexpected response: missing product_type_config data');
    }

    return rawData.product_type_config;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

/**
 * Update an existing ProductTypeConfig.
 *
 * Route: `PUT /api/v1/admin/product-type-configs/{id}`
 * Auth:  `auth:console`, scope `products:write`
 */
export async function updateProductTypeConfig(
  id: number,
  payload: UpdateProductTypeConfigPayload,
): Promise<ProductTypeConfigDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<Record<string, unknown>>>(
      `/admin/product-type-configs/${id}`,
      payload,
    );

    const envelope = res.data;
    const rawData =
      envelope.data &&
      typeof envelope.data === 'object' &&
      'product_type_config' in envelope.data
        ? (envelope.data as unknown as ProductTypeConfigResponseData)
        : null;

    if (!rawData?.product_type_config) {
      throw new Error('Unexpected response: missing product_type_config data');
    }

    return rawData.product_type_config;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Status toggle
// ---------------------------------------------------------------------------

/**
 * Toggle the active status of a product type config.
 *
 * Route: `PATCH /api/v1/admin/product-type-configs/{id}/status`
 * Auth:  `auth:console`, scope `products:write`
 */
export async function updateAdminProductTypeConfigStatus(
  id: number,
  isActive: boolean,
): Promise<ProductTypeConfigDTO> {
  try {
    const res = await apiClient.patch<ApiEnvelope<Record<string, unknown>>>(
      `/admin/product-type-configs/${id}/status`,
      { is_active: isActive },
    );

    const envelope = res.data;
    const rawData =
      envelope.data &&
      typeof envelope.data === 'object' &&
      'product_type_config' in envelope.data
        ? (envelope.data as unknown as ProductTypeConfigResponseData)
        : null;

    if (!rawData?.product_type_config) {
      throw new Error('Unexpected response: missing product_type_config data');
    }

    return rawData.product_type_config;
  } catch (err) {
    throwAsApiError(err);
  }
}
